import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileMenu from './ProfileMenu';
import { getUserProfile } from '../utils/localStorage';
import './Header.css';

const Header = ({ onMenuClick, onSearch }) => {
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [userProfile, setUserProfile] = useState({ name: 'Guest', profilePic: null });
  const navigate = useNavigate();
  const suggestionRef = useRef(null);
  
  useEffect(() => {
    const profile = getUserProfile();
    setUserProfile(profile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchInput.trim().length > 1) {
        // Use local suggestions instead of external API to avoid CORS
        const localSuggestions = [
          `${searchInput} tutorial`,
          `${searchInput} review`,
          `${searchInput} 2024`,
          `${searchInput} explained`,
          `${searchInput} best`,
          `${searchInput} trending`,
          `${searchInput} latest`,
          `${searchInput} top`
        ];
        setSuggestions(localSuggestions.slice(0, 5));
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [searchInput]);

  const handleSearch = (e, query = null) => {
    e.preventDefault();
    const searchQuery = query || searchInput;
    if (searchQuery.trim()) {
      onSearch(searchQuery);
      navigate(`/search?q=${searchQuery}`);
      setShowSuggestions(false);
      setSearchInput(searchQuery);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchInput(suggestion);
    onSearch(suggestion);
    navigate(`/search?q=${suggestion}`);
    setShowSuggestions(false);
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-btn" onClick={onMenuClick} aria-label="Toggle menu">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor"/>
          </svg>
        </button>
        <div className="logo" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" fill="#FFD700"/>
          </svg>
          <span className="logo-text">RaiTube</span>
        </div>
      </div>

      <form className={`search-container ${showMobileSearch ? 'mobile-search-active' : ''}`} onSubmit={handleSearch}>
        <div className="search-wrapper" ref={suggestionRef}>
          <input
            type="text"
            className="search-input"
            placeholder="Search videos..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          />
          <button type="submit" className="search-btn" aria-label="Search">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
            </svg>
          </button>
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                  <span>{suggestion}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>

      <div className="header-right">
        <button 
          className="icon-btn mobile-search-btn" 
          aria-label="Search"
          onClick={() => setShowMobileSearch(!showMobileSearch)}
        >
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
          </svg>
        </button>
        <button className="icon-btn desktop-only" aria-label="Notifications">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" fill="currentColor"/>
          </svg>
        </button>
        <div className="user-avatar" onClick={() => setShowProfileMenu(!showProfileMenu)}>
          {userProfile.profilePic ? (
            <img src={userProfile.profilePic} alt={userProfile.name} />
          ) : (
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
            </svg>
          )}
        </div>
        <ProfileMenu 
          isOpen={showProfileMenu} 
          onClose={() => setShowProfileMenu(false)} 
        />
      </div>
    </header>
  );
};

export default Header;
